"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchInput } from "@/fetch-ui/FetchInput";
import { ShieldCheck } from "lucide-react";
import { useState } from "react";
import { signInWithSSO } from "../../_logic/auth";

interface SSOSignInProps {
  redirectTo?: string;
}

export const SSOSignIn = ({ redirectTo }: SSOSignInProps) => {
  // States
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [domain, setDomain] = useState<string>("");
  const [showDomainInput, setShowDomainInput] = useState<boolean>(false);

  // Handlers
  const handleButtonClick = () => {
    setShowDomainInput(true);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!domain.trim()) {
      setError("Please enter your company domain");
      return;
    }

    setIsLoading(true);
    try {
      const result = await signInWithSSO(domain, redirectTo);
      if ("error" in result) {
        setError(result.error);
        setIsLoading(false);
      } else {
        window.location.href = result.url;
      }
    } catch {
      setError("Something went wrong. Please try again.");
      setIsLoading(false);
    }
  };

  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDomain(e.target.value.trim());
    setError(null);
  };

  // Render
  if (!showDomainInput) {
    return (
      <div className="w-full">
        <button
          className="flex flex-row gap-2 items-center justify-center rounded-lg border border-gray-200 p-2 w-full shadow-sm button-hover disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleButtonClick}
          disabled={isLoading}
        >
          <ShieldCheck className="size-4 text-gray-600" />
          <p className="text-sm font-medium text-gray-500">Continue with SSO</p>
        </button>
        {error && <p className="text-sm text-red-500 font-medium mt-2">{error}</p>}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full flex flex-col gap-2">
      <div className="flex flex-row gap-2 items-start">
        <div className="flex-1 min-w-0">
          <FetchInput
            id="sso-domain"
            type="text"
            value={domain}
            onChange={handleDomainChange}
            placeholder="company.com"
            disabled={isLoading}
            error={!!error}
            autoFocus
            className="w-full"
          />
        </div>
        <div className="flex-shrink-0 pt-0.5">
          <FetchButton
            type="submit"
            variant="primary"
            size="sm"
            loading={isLoading}
            disabled={!domain.trim()}
            className="whitespace-nowrap"
          >
            Continue
          </FetchButton>
        </div>
      </div>
      {(error || !error) && (
        <p className={error ? "text-xs font-medium text-red-600" : "text-xs font-medium text-gray-600"}>
          {error || "Enter your company email domain (e.g., company.com) to sign in with SSO"}
        </p>
      )}
    </form>
  );
};
